import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  redirect,
  useParams,
  useRouterState,
  useSearch,
} from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type FocusEvent } from 'react';
import { api } from './api/client';
import { getSession, setCompany, setSession, takeCameBack } from './auth/session';
import { Button } from './components/Button';
import { ErrorState, isChunkFailure } from './components/ErrorState';
import { Skeleton } from './components/Skeleton';
import { NotFound } from './components/NotFound';
import { Stack } from './components/layout/Stack';
import {
  forgetCompanyContext,
  loadCompanyContext,
  useCompanyContext,
} from './modules/context';
import { LANG_LABEL, langFor, setLang, t, tLabel, useLang, type Lang } from './modules/i18n';
import { langOf } from '@workbook/shared';
import { groupedNav, groupOf } from './modules/nav-groups';
// DS-11b3: the kit's icons (Lucide's geometry) and the offline pill, each by its own file (plan §0.1).
import { Icon } from './components/Icon';
import { OFFLINE, OnlinePill } from './components/OnlinePill';
import { CompanyFormatProvider, useMemberships } from './modules/company-format-provider';
import { RouteLinkContext } from './components/layout/route-link';
import { listSearch, useListSearch } from './api/useListSearch';
// DS-11a1: by its own file, never through kit.tsx — the console still imports the barrel (plan §0.1).
import { SkipLink, useFocusMainOnNavigate } from './components/SkipLink';
// DS-11a3: the user menu's button, by its own file too; its panel (and theme.ts) is a lazy chunk of its own.
import { UserMenu } from './components/UserMenu';
import { Wordmark } from './components/Wordmark';
import { ListSearchContext } from './components/layout/list-search';
/**
 * 🔧 DS-10d: `React.lazy`'s `{ default: X }` ("EVERY SCREEN IS LAZY", below), built here once rather than as an object
 * literal in each of the ~140 factories. This table is on the back office's first load, where the literal cost 9 bytes
 * a screen, about 1.2 KB: what DS-10d's list validators and `ListSearchContext` provider add to that load is paid here.
 */
const asDefault = <C,>(component: C) => ({ default: component });
// DS-11b4 (F600): the release notes and what this browser has read of them — a chunk of their own, asked for once drawn.
const whatsNew = () => import('./whats-new');
// Loaded when first opened: the search is one keystroke away, and nobody needs it before the page draws.
const CommandPalette = lazy(() => import('./palette/CommandPalette').then((m) => asDefault(m.CommandPalette)));
import { ModuleDisabledScreen } from './screens/ModuleDisabled';

/**
 * 🔴 EVERY SCREEN IS LAZY — ERP Phase 1a increment 3.
 *
 * ## ⚠️ WHAT THIS REPLACED, AND WHAT IT COST
 *
 * All 102 screens were STATIC imports, so the browser downloaded the whole product to render any
 * part of it: **one chunk, 792 KB raw**. A cashier opening the till downloaded payroll; a shop with
 * no warranty module downloaded the service-centre screens it can never reach.
 *
 * 🔬 §1a's exit asks for *"the initial chunk under 250 KB AND each module its own chunk"*, and
 * the second half is the half with work behind it — gzipped, the first was already met before this
 * phase began. `vite.config.ts` groups these dynamic imports by module using
 * `src/modules/screen-modules.ts`, which is GENERATED from the server's own `@RequiresModule`
 * decorators rather than typed out from screen names.
 *
 * 🔧 `.then((m) => asDefault(m.X))` is `React.lazy`'s contract: it wants a module with a DEFAULT
 * export and these screens are named exports, which is the right way round — a default export is a
 * name the importer chooses, and two screens imported under one name is a mistake nothing catches.
 *
 * ⚠️ THE `Suspense` BOUNDARY IS IN `Shell`, around the `Outlet`. Without one, the first navigation
 * to any lazy route throws *"A component suspended while responding to synchronous input"* and the
 * page goes white — which is the failure mode of forgetting it, and it does not appear until a chunk
 * is actually slow enough to be observed.
 */
/**
 * DS-10d (scope §3.2): a DocumentList route's filters live in its search — `status`, `from`, `to` (and `q`), plus
 * the list's own extras (Activities `kind`/`done`, Leads `source`, PostDatedCheques `direction`). A hand-edited URL
 * can put nothing else there, and nothing that would be a 400. The cursor is never a search param.
 * list-routes.spec.ts holds every list route to this.
 */
const documentListSearch = listSearch();

const InvoicesScreen = lazy(() => import('./screens/Invoices').then((m) => asDefault(m.InvoicesScreen)));
const InvoiceScreen = lazy(() => import('./screens/Invoice').then((m) => asDefault(m.InvoiceScreen)));
const InvoiceEditScreen = lazy(() => import('./screens/InvoiceEdit').then((m) => asDefault(m.InvoiceEditScreen)));
const InvoiceCreateScreen = lazy(() => import('./screens/InvoiceCreate').then((m) => asDefault(m.InvoiceCreateScreen)));
const BillsScreen = lazy(() => import('./screens/Bills').then((m) => asDefault(m.BillsScreen)));
const PurchaseOrdersScreen = lazy(() => import('./screens/PurchaseOrders').then((m) => asDefault(m.PurchaseOrdersScreen)));
const PurchaseOrderScreen = lazy(() => import('./screens/PurchaseOrder').then((m) => asDefault(m.PurchaseOrderScreen)));
const PurchaseOrderCreateScreen = lazy(() => import('./screens/PurchaseOrderCreate').then((m) => asDefault(m.PurchaseOrderCreateScreen)));
const ReceivedNotBilledScreen = lazy(() => import('./screens/ReceivedNotBilled').then((m) => asDefault(m.ReceivedNotBilledScreen)));
const GoodsReceiptsScreen = lazy(() => import('./screens/GoodsReceipts').then((m) => asDefault(m.GoodsReceiptsScreen)));
const GoodsReceiptScreen = lazy(() => import('./screens/GoodsReceipt').then((m) => asDefault(m.GoodsReceiptScreen)));
const GoodsReceiptCreateScreen = lazy(() => import('./screens/GoodsReceiptCreate').then((m) => asDefault(m.GoodsReceiptCreateScreen)));
const PostDatedChequesScreen = lazy(() => import('./screens/PostDatedCheques').then((m) => asDefault(m.PostDatedChequesScreen)));
const PostDatedChequeScreen = lazy(() => import('./screens/PostDatedCheque').then((m) => asDefault(m.PostDatedChequeScreen)));
const PostDatedChequeCreateScreen = lazy(() => import('./screens/PostDatedChequeCreate').then((m) => asDefault(m.PostDatedChequeCreateScreen)));
const FixedAssetsScreen = lazy(() => import('./screens/FixedAssets').then((m) => asDefault(m.FixedAssetsScreen)));
const FixedAssetScreen = lazy(() => import('./screens/FixedAsset').then((m) => asDefault(m.FixedAssetScreen)));
const DepreciationRunsScreen = lazy(() => import('./screens/DepreciationRuns').then((m) => asDefault(m.DepreciationRunsScreen)));
const BlanketOrdersScreen = lazy(() => import('./screens/BlanketOrders').then((m) => asDefault(m.BlanketOrdersScreen)));
const BlanketOrderScreen = lazy(() => import('./screens/BlanketOrder').then((m) => asDefault(m.BlanketOrderScreen)));
const LettersOfCreditScreen = lazy(() => import('./screens/LettersOfCredit').then((m) => asDefault(m.LettersOfCreditScreen)));
const ExportLcsScreen = lazy(() => import('./screens/ExportLcs').then((m) => asDefault(m.ExportLcsScreen)));
const LetterOfCreditScreen = lazy(() => import('./screens/LetterOfCredit').then((m) => asDefault(m.LetterOfCreditScreen)));
const LandedCostsScreen = lazy(() => import('./screens/LandedCosts').then((m) => asDefault(m.LandedCostsScreen)));
const LandedCostScreen = lazy(() => import('./screens/LandedCost').then((m) => asDefault(m.LandedCostScreen)));
const StockTransfersScreen = lazy(() => import('./screens/StockTransfers').then((m) => asDefault(m.StockTransfersScreen)));
const StockTransferScreen = lazy(() => import('./screens/StockTransfer').then((m) => asDefault(m.StockTransferScreen)));
const StockValuationScreen = lazy(() => import('./screens/StockValuation').then((m) => asDefault(m.StockValuationScreen)));
const StockExpiryScreen = lazy(() => import('./screens/StockExpiry').then((m) => asDefault(m.StockExpiryScreen)));
const PharmaScreen = lazy(() => import('./screens/Pharma').then((m) => asDefault(m.PharmaScreen)));
const StockCountsScreen = lazy(() => import('./screens/StockCounts').then((m) => asDefault(m.StockCountsScreen)));
const StockCountScreen = lazy(() => import('./screens/StockCount').then((m) => asDefault(m.StockCountScreen)));
const StockAdjustmentsScreen = lazy(() => import('./screens/StockAdjustments').then((m) => asDefault(m.StockAdjustmentsScreen)));
const StockAdjustmentScreen = lazy(() => import('./screens/StockAdjustment').then((m) => asDefault(m.StockAdjustmentScreen)));
const BillScreen = lazy(() => import('./screens/Bill').then((m) => asDefault(m.BillScreen)));
const BillEditScreen = lazy(() => import('./screens/BillEdit').then((m) => asDefault(m.BillEditScreen)));
const BillCreateScreen = lazy(() => import('./screens/BillCreate').then((m) => asDefault(m.BillCreateScreen)));
const JournalsScreen = lazy(() => import('./screens/Journals').then((m) => asDefault(m.JournalsScreen)));
const JournalScreen = lazy(() => import('./screens/Journal').then((m) => asDefault(m.JournalScreen)));
const PeriodsScreen = lazy(() => import('./screens/Periods').then((m) => asDefault(m.PeriodsScreen)));
const ApprovalsScreen = lazy(() => import('./screens/Approvals').then((m) => asDefault(m.ApprovalsScreen)));
const ProjectsScreen = lazy(() => import('./screens/Projects').then((m) => asDefault(m.ProjectsScreen)));
const PayrollRunsScreen = lazy(() => import('./screens/PayrollRuns').then((m) => asDefault(m.PayrollRunsScreen)));
const PayrollRunScreen = lazy(() => import('./screens/PayrollRun').then((m) => asDefault(m.PayrollRunScreen)));
const RecurringCostsScreen = lazy(() => import('./screens/RecurringCosts').then((m) => asDefault(m.RecurringCostsScreen)));
const ProjectScreen = lazy(() => import('./screens/Project').then((m) => asDefault(m.ProjectScreen)));
const ActivityTypesScreen = lazy(() => import('./screens/ActivityTypes').then((m) => asDefault(m.ActivityTypesScreen)));
const LoansFinesScreen = lazy(() => import('./screens/LoansFines').then((m) => asDefault(m.LoansFinesScreen)));
const MyPayslipsScreen = lazy(() => import('./screens/MyPayslips').then((m) => asDefault(m.MyPayslipsScreen)));
const HolidaysScreen = lazy(() => import('./screens/Holidays').then((m) => asDefault(m.HolidaysScreen)));
const WageBoardsScreen = lazy(() => import('./screens/WageBoards').then((m) => asDefault(m.WageBoardsScreen)));
const AttendanceScreen = lazy(() => import('./screens/Attendance').then((m) => asDefault(m.AttendanceScreen)));
const ShiftsScreen = lazy(() => import('./screens/Shifts').then((m) => asDefault(m.ShiftsScreen)));
const OrganisationScreen = lazy(() => import('./screens/Organisation').then((m) => asDefault(m.OrganisationScreen)));
const EmployeesScreen = lazy(() => import('./screens/Employees').then((m) => asDefault(m.EmployeesScreen)));
const AuditLogScreen = lazy(() => import('./screens/AuditLog').then((m) => asDefault(m.AuditLogScreen)));
const OperatorTrailScreen = lazy(() => import('./screens/OperatorTrail').then((m) => asDefault(m.OperatorTrailScreen)));
const BankStatementsScreen = lazy(() => import('./screens/BankStatements').then((m) => asDefault(m.BankStatementsScreen)));
const BankStatementScreen = lazy(() => import('./screens/BankStatement').then((m) => asDefault(m.BankStatementScreen)));
const AccountsScreen = lazy(() => import('./screens/Accounts').then((m) => asDefault(m.AccountsScreen)));
const TaxCodesScreen = lazy(() => import('./screens/TaxCodes').then((m) => asDefault(m.TaxCodesScreen)));
const UsersScreen = lazy(() => import('./screens/Users').then((m) => asDefault(m.UsersScreen)));
const SettingsScreen = lazy(() => import('./screens/Settings').then((m) => asDefault(m.SettingsScreen)));
const SettingsCompanyScreen = lazy(() => import('./screens/SettingsCompany').then((m) => asDefault(m.SettingsCompanyScreen)));
const RegistersScreen = lazy(() => import('./screens/Registers').then((m) => asDefault(m.RegistersScreen)));
const SessionsScreen = lazy(() => import('./screens/Sessions').then((m) => asDefault(m.SessionsScreen)));
const StockTieScreen = lazy(() => import('./screens/StockTie').then((m) => asDefault(m.StockTieScreen)));
const StockMovementScreen = lazy(() => import('./screens/StockMovement').then((m) => asDefault(m.StockMovementScreen)));
const TrackingScreen = lazy(() => import('./screens/Tracking').then((m) => asDefault(m.TrackingScreen)));
const ConversionScreen = lazy(() => import('./screens/Conversion').then((m) => asDefault(m.ConversionScreen)));
const SettingsNumberingScreen = lazy(() => import('./screens/SettingsNumbering').then((m) => asDefault(m.SettingsNumberingScreen)));
const SettingsAccountsScreen = lazy(() => import('./screens/SettingsAccounts').then((m) => asDefault(m.SettingsAccountsScreen)));
const SettingsApprovalsScreen = lazy(() => import('./screens/SettingsApprovals').then((m) => asDefault(m.SettingsApprovalsScreen)));
const SettingsMembersScreen = lazy(() => import('./screens/SettingsMembers').then((m) => asDefault(m.SettingsMembersScreen)));
const SettingsPortalScreen = lazy(() => import('./screens/SettingsPortal').then((m) => asDefault(m.SettingsPortalScreen)));
const SettingsPreferencesScreen = lazy(() => import('./screens/SettingsPreferences').then((m) => asDefault(m.SettingsPreferencesScreen)));
const ReportsScreen = lazy(() => import('./screens/Reports').then((m) => asDefault(m.ReportsScreen)));
const TrialBalanceScreen = lazy(() => import('./screens/TrialBalance').then((m) => asDefault(m.TrialBalanceScreen)));
const ProfitAndLossScreen = lazy(() => import('./screens/ProfitAndLoss').then((m) => asDefault(m.ProfitAndLossScreen)));
const BalanceSheetScreen = lazy(() => import('./screens/BalanceSheet').then((m) => asDefault(m.BalanceSheetScreen)));
const StockDashboardScreen = lazy(() => import('./screens/StockDashboard').then((m) => asDefault(m.StockDashboardScreen)));
const SalesDashboardScreen = lazy(() => import('./screens/SalesDashboard').then((m) => asDefault(m.SalesDashboardScreen)));
const ReorderReportScreen = lazy(() => import('./screens/ReorderReport').then((m) => asDefault(m.ReorderReportScreen)));
const ConsolidatedStatementsScreen = lazy(() =>
  import('./screens/ConsolidatedStatements').then((m) => asDefault(m.ConsolidatedStatementsScreen)),
);
const BomsScreen = lazy(() => import('./screens/Boms').then((m) => asDefault(m.BomsScreen)));
const StorePaymentsScreen = lazy(() => import('./screens/StorePayments').then((m) => asDefault(m.StorePaymentsScreen)));
const SubscriptionsScreen = lazy(() => import('./screens/Subscriptions').then((m) => asDefault(m.SubscriptionsScreen)));
const MarketplaceScreen = lazy(() => import('./screens/Marketplace').then((m) => asDefault(m.MarketplaceScreen)));
const CouriersScreen = lazy(() => import('./screens/Couriers').then((m) => asDefault(m.CouriersScreen)));
const ItemsScreen = lazy(() => import('./screens/Items').then((m) => asDefault(m.ItemsScreen)));
const SettingsDocumentsScreen = lazy(() => import('./screens/SettingsDocuments').then((m) => asDefault(m.SettingsDocumentsScreen)));
const SettingsWorkflowRulesScreen = lazy(() => import('./screens/SettingsWorkflowRules').then((m) => asDefault(m.SettingsWorkflowRulesScreen)));
const SettingsApiTokensScreen = lazy(() => import('./screens/SettingsApiTokens').then((m) => asDefault(m.SettingsApiTokensScreen)));
const SettingsWebhooksScreen = lazy(() => import('./screens/SettingsWebhooks').then((m) => asDefault(m.SettingsWebhooksScreen)));
const SettingsCustomFieldsScreen = lazy(() =>
  import('./screens/SettingsCustomFields').then((m) => asDefault(m.SettingsCustomFieldsScreen)),
);
const VatByPlaceScreen = lazy(() => import('./screens/VatByPlace').then((m) => asDefault(m.VatByPlaceScreen)));
const OwnerSummaryScreen = lazy(() => import('./screens/OwnerSummary').then((m) => asDefault(m.OwnerSummaryScreen)));
const GroupDashboardScreen = lazy(() => import('./screens/GroupDashboard').then((m) => asDefault(m.GroupDashboardScreen)));
const InterBranchTransferScreen = lazy(() =>
  import('./screens/InterBranchTransfer').then((m) => asDefault(m.InterBranchTransferScreen)),
);
const GeneralLedgerScreen = lazy(() => import('./screens/GeneralLedger').then((m) => asDefault(m.GeneralLedgerScreen)));
const VatReturnScreen = lazy(() => import('./screens/VatReturn').then((m) => asDefault(m.VatReturnScreen)));
const TurnoverReturnScreen = lazy(() => import('./screens/TurnoverReturn').then((m) => asDefault(m.TurnoverReturnScreen)));
const VatSummaryScreen = lazy(() => import('./screens/VatSummary').then((m) => asDefault(m.VatSummaryScreen)));
const StatementScreen = lazy(() => import('./screens/Statement').then((m) => asDefault(m.StatementScreen)));
const AgingScreen = lazy(() => import('./screens/Aging').then((m) => asDefault(m.AgingScreen)));
const PaymentsScreen = lazy(() => import('./screens/Payments').then((m) => asDefault(m.PaymentsScreen)));
const PaymentScreen = lazy(() => import('./screens/Payment').then((m) => asDefault(m.PaymentScreen)));
const PaymentCreateScreen = lazy(() => import('./screens/PaymentCreate').then((m) => asDefault(m.PaymentCreateScreen)));
const PaymentEditScreen = lazy(() => import('./screens/PaymentEdit').then((m) => asDefault(m.PaymentEditScreen)));
const CreditNotesScreen = lazy(() => import('./screens/CreditNotes').then((m) => asDefault(m.CreditNotesScreen)));
const CreditNoteScreen = lazy(() => import('./screens/CreditNote').then((m) => asDefault(m.CreditNoteScreen)));
const CreditNoteCreateScreen = lazy(() => import('./screens/CreditNoteCreate').then((m) => asDefault(m.CreditNoteCreateScreen)));
const CreditNoteEditScreen = lazy(() => import('./screens/CreditNoteEdit').then((m) => asDefault(m.CreditNoteEditScreen)));
const ExpensesScreen = lazy(() => import('./screens/Expenses').then((m) => asDefault(m.ExpensesScreen)));
const ExpenseScreen = lazy(() => import('./screens/Expense').then((m) => asDefault(m.ExpenseScreen)));
const ExpenseCreateScreen = lazy(() => import('./screens/ExpenseCreate').then((m) => asDefault(m.ExpenseCreateScreen)));
const ExpenseEditScreen = lazy(() => import('./screens/ExpenseEdit').then((m) => asDefault(m.ExpenseEditScreen)));
const JournalDraftsScreen = lazy(() => import('./screens/JournalDrafts').then((m) => asDefault(m.JournalDraftsScreen)));
const JournalDraftScreen = lazy(() => import('./screens/JournalDraft').then((m) => asDefault(m.JournalDraftScreen)));
const JournalDraftFormScreen = lazy(() => import('./screens/JournalDraftForm').then((m) => asDefault(m.JournalDraftFormScreen)));
const VatAdjustmentsScreen = lazy(() => import('./screens/VatAdjustments').then((m) => asDefault(m.VatAdjustmentsScreen)));
const VatAdjustmentScreen = lazy(() => import('./screens/VatAdjustment').then((m) => asDefault(m.VatAdjustmentScreen)));
const VatAdjustmentFormScreen = lazy(() => import('./screens/VatAdjustmentForm').then((m) => asDefault(m.VatAdjustmentFormScreen)));
const TurnoverTaxAdjustmentsScreen = lazy(() => import('./screens/TurnoverTaxAdjustments').then((m) => asDefault(m.TurnoverTaxAdjustmentsScreen)));
const TurnoverTaxAdjustmentScreen = lazy(() => import('./screens/TurnoverTaxAdjustment').then((m) => asDefault(m.TurnoverTaxAdjustmentScreen)));
const TurnoverTaxAdjustmentFormScreen = lazy(() => import('./screens/TurnoverTaxAdjustmentForm').then((m) => asDefault(m.TurnoverTaxAdjustmentFormScreen)));
/**
 * DS-11b2 (F483; plan C9.7): the page a company opens on, asked for AHEAD of its route — while "Choose a company" is on
 * screen (App.tsx) and when the shell first mounts — so the shell's paint is not followed by a wait for its first page.
 * The same specifier as the route's import, so the browser fetches the chunk once. ⚠️ The route keeps its own literal
 * `lazy(() => import('./screens/…'))`: scripts/gen-screen-modules.mjs reads that shape to join `/` to its module.
 */
export const prefetchDashboard = () => import('./screens/Dashboard');
const DashboardScreen = lazy(() => import('./screens/Dashboard').then((m) => asDefault(m.DashboardScreen)));
const PartnersScreen = lazy(() => import('./screens/Partners').then((m) => asDefault(m.PartnersScreen)));
const LeadsScreen = lazy(() => import('./screens/Leads').then((m) => asDefault(m.LeadsScreen)));
const ActivitiesScreen = lazy(() => import('./screens/Activities').then((m) => asDefault(m.ActivitiesScreen)));
const Partner360Screen = lazy(() => import('./screens/Partner360').then((m) => asDefault(m.Partner360Screen)));
const PipelineScreen = lazy(() => import('./screens/Pipeline').then((m) => asDefault(m.PipelineScreen)));
const FollowUpsScreen = lazy(() => import('./screens/FollowUps').then((m) => asDefault(m.FollowUpsScreen)));
const PartnerDocumentExpiryScreen = lazy(() => import('./screens/PartnerDocumentExpiry').then((m) => asDefault(m.PartnerDocumentExpiryScreen)));
const OrderBookScreen = lazy(() => import('./screens/OrderBook').then((m) => asDefault(m.OrderBookScreen)));

/**
 * Routes.
 *
 * A real URL per screen, not a tab state, and the reason is specific to this product rather than
 * general tidiness: an accountant works with a colleague and a document reference in hand. "Look at
 * INV-2026-0148" has to be a link somebody can send, and a page they can reload, bookmark and open
 * in a second tab beside another one. Caddy already serves the shell for any path, so a deep link
 * lands correctly.
 */

/**
 * The shell IS the root route, rather than chrome wrapped around a `RouterProvider`.
 *
 * It has to be: `<Link>` calls `useRouter`, so a navigation rail rendered outside the provider
 * throws "useRouter must be used inside a RouterProvider" and takes the whole page down. Putting
 * the chrome in the root component puts it inside the router, where its own links work.
 */
export function Shell() {
  // DS-7a: on the shared key, so the company format provider around the shell reads this same answer.
  const memberships = useMemberships().data ?? [];
  const session = getSession();
  /**
   * ⚠️ `undefined` while it is in flight and `null` when it could not be read, and the nav treats
   * BOTH as *show everything*. A nav that emptied itself for a moment on every page load, or
   * whenever the network hiccuped, would look exactly like the product having lost the customer's
   * data — and the API refuses anything they have not bought regardless.
   */
  const context = useCompanyContext();
  /**
   * 🔴 §1a's THIRD EXIT CRITERION LIVES ON THIS LINE. `useLang` subscribes through
   * `useSyncExternalStore`, so `setLang` re-renders every subscriber — which is what *"the Bangla
   * toggle RE-RENDERS the shell's chrome"* asks for. A module-level variable read once would show
   * the right words on a reload and satisfy a looser reading of the same sentence.
   */
  const lang = useLang();

  const current = memberships.find((m) => m.id === session?.companyId);

  const signOut = async () => {
    const s = getSession();
    if (s) await api.logout(s.refreshToken).catch(() => undefined);
    setSession(null);
  };

  /**
   * 🔴 THE SHELL, REDESIGNED (owner, 2026-09-25): a sidebar grouped by department, a top bar with search,
   * and content that uses the screen. The owner saw forty-seven sections in one row of tabs and a page
   * squeezed into 1,180px of a 2,000px monitor.
   *
   * ⚠️ The sidebar remembers, for this person on this browser, whether it is collapsed and which groups
   * are open — a convenience, so every read and write is guarded and the shell works without it.
   */
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // DS-11b2 (F482): built again only when the company's context changes, not on every navigation.
  const groups = useMemo(() => groupedNav(context), [context]);
  const activeGroup = groupOf(pathname);
  const [collapsed, setCollapsed] = useState(() => remembered('wb.side.collapsed') === '1');
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set((remembered('wb.side.open') ?? '').split(',').filter(Boolean)));
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  // DS-11b4 (F600): a dot on Account while the newest What's new is unread in this browser (whats-new.ts).
  const [fresh, setFresh] = useState(false);
  useEffect(() => void whatsNew().then((m) => setFresh(m.unseen()), () => undefined), []);
  /**
   * DS-11b4 (F600): unread notes — a dot on Account, and its word for a screen reader ("Account (New)"). The space is
   * the button's own text node: one inside the dot is dropped from the name ("Account(New)"). Built here, not inline in
   * the bar, so the bar's markup stays a row of controls (ui-gates.spec.ts reads the user menu as its last).
   */
  const unread = fresh ? (
    <>
      {' '}
      <span className="wb-user__dot">
        <span className="wb-sr-only">({t(lang, 'New')})</span>
      </span>
    </>
  ) : null;

  const isOpen = (key: string) => open.has(key) || key === activeGroup;
  const toggleGroup = (key: string) => {
    const next = new Set(open);
    if (isOpen(key)) next.delete(key);
    else next.add(key);
    setOpen(next);
    remember('wb.side.open', [...next].join(','));
  };
  const toggleCollapsed = () => {
    remember('wb.side.collapsed', collapsed ? '0' : '1');
    setCollapsed(!collapsed);
  };

  // A drawer left open over the page it just navigated to would hide that page.
  useEffect(() => setDrawer(false), [pathname]);
  // DS-11a1 (§1.1): and the new page is where focus goes, not the link that was clicked (or back to the Menu button).
  useFocusMainOnNavigate(pathname);
  // DS-11a4 review (F53): Cancel on "Choose a company" comes back to the company button it left from, not to <body>.
  const companyButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (takeCameBack()) companyButton.current?.focus();
  }, []);
  // DS-11b2 (F483, plan C9.7): a session restored on a reload never passes "Choose a company", so the shell asks too.
  useEffect(() => void prefetchDashboard(), []);
  /**
   * 🔴 THE DRAWER AND THE KEYBOARD — DS-11a1 (§1.2; F52, F535). Opening it puts focus on its close button; closing it
   * (Escape, the close button, the scrim) puts focus back on Menu, where it came from. Escape is listened for only
   * while it is open, so an Escape anywhere else (a field, a dialog) never moves focus to a button that is not there.
   * A route change closes it too, and there focus goes to the page (above), not back to Menu.
   */
  const menu = useRef<HTMLButtonElement>(null);
  const shut = useRef<HTMLButtonElement>(null);
  const close = () => {
    setDrawer(false);
    menu.current?.focus();
  };
  /**
   * 🔴 TAB OUT OF THE OPEN DRAWER CLOSES IT — DS-11a1 review (WCAG 2.4.11). It is not a modal, so Tab may leave it, and
   * the next stop is Menu, which the open drawer covers: focus nobody could see. Leaving closes it and moves no focus.
   * ⚠️ Only when focus goes SOMEWHERE (`relatedTarget`): a click on the drawer's blank space, or the window losing
   * focus, blurs with none, and neither is leaving.
   */
  const leave = (e: FocusEvent<HTMLElement>) => {
    const to = e.relatedTarget;
    if (drawer && to instanceof Node && !e.currentTarget.contains(to)) setDrawer(false);
  };
  useEffect(() => {
    if (!drawer) return;
    shut.current?.focus();
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [drawer]);
  /**
   * Ctrl+K / ⌘K, from anywhere. DS-11a2 (§1.4; F557), and its review: the LETTER where the layout types Latin letters
   * (QWERTY, Dvorak, Colemak: the key that types k), the KEY'S PLACE (`code`) where it does not (a Bangla layout, where
   * K types ক) — so Dvorak's Ctrl+T, typed on the key in K's place, stays the browser's. Never while a dialog is open —
   * the palette would open behind a question that has made the page inert. ⚠️ The key is swallowed all the same
   * (DS-11a2 review): passed on, Chrome and Firefox would put their own search in the address bar — and the open
   * palette is itself a dialog[open].
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (/^[a-z]$/i.test(e.key) ? e.key.toLowerCase() !== 'k' : e.code !== 'KeyK') return;
      e.preventDefault();
      if (!document.querySelector('dialog[open]')) setPalette(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const nameLang = current ? (langOf(current.name) ?? (lang === 'bn' ? 'en' : undefined)) : undefined;
  /**
   * 🔴 ONE USER MENU — DS-11a3 (§1.5; owner, 2026-10-07). All companies (ERP Phase 13.3: only somebody with more than
   * one company has a group to look across), Summary (13.8: the owner's page on a phone) and Settings (F65: a way
   * there that does not depend on the sidebar) left the bar for the menu with Sign out and the theme, which is what
   * lets the bar fit a phone in two rows. Labels are passed in: the menu is shared with the portal and the console,
   * and i18n.ts must reach neither (plan C1).
   */
  const menuLinks = [
    ...(memberships.length > 1 ? [{ label: t(lang, 'All companies'), to: '/group' as const }] : []),
    { label: t(lang, 'Summary'), to: '/summary' as const },
    { label: t(lang, 'Settings'), to: '/settings' as const },
  ];

  return (
    <div className={`app${collapsed ? ' app--collapsed' : ''}${drawer ? ' app--drawer' : ''}`}>
      {/* DS-11a1: the first thing Tab reaches, past the sidebar and the bar to the page (WCAG 2.4.1). */}
      <SkipLink label={t(lang, 'Skip to content')} lang={lang} />
      {/*
        🔴 THE NAV IS FILTERED BY WHAT THIS COMPANY HAS TURNED ON — ERP Phase 1a increment 4, now inside
        `groupedNav`. ⚠️ Absent rather than greyed: a greyed row advertises what the client has not bought.
      */}
      {/*
        🔴 THE CHROME SAYS WHICH LANGUAGE IT IS IN — DS-4 (WCAG 3.1.2). In Bangla the sidebar, the bar and the palette
        are `lang="bn"`, which is also what switches the Bangla type layer on for them; the screens stay English, so
        the page's own <html lang> does not move. An English label inside Bangla chrome says `lang="en"` (langFor).
      */}
      <aside className="app-side" id="app-side" lang={lang} onBlur={leave}>
        <div className="app-side__brand" lang="en">
          {/* DS-11b1: the app's name, drawn as every app draws it (the accent square is decoration). */}
          <Wordmark word="Workbook" />
          {/* DS-11a1: the drawer's own way out (shown at 960 and under). Inside the English brand, so it says its language. */}
          <button type="button" className="app-side__close" ref={shut} lang={lang} aria-label={t(lang, 'Close menu')} onClick={close}>
            <Icon name="x" />
          </button>
        </div>
        <nav className="app-side__nav" aria-label={t(lang, 'Sections')}>
          {groups.map((group) => (
            <div key={group.key} className={`app-group${group.key === activeGroup ? ' app-group--on' : ''}`}>
              <button
                type="button"
                className="app-group__head"
                aria-expanded={isOpen(group.key)}
                // DS-11a1 (F407): names the panel only while it exists — it is not rendered closed.
                aria-controls={isOpen(group.key) && !collapsed ? `nav-${group.key}` : undefined}
                title={t(lang, group.label)}
                onClick={() => (collapsed ? toggleCollapsed() : toggleGroup(group.key))}
              >
                <Icon name={group.icon} />
                <span className="app-group__label">{t(lang, group.label)}</span>
                <Icon name="chevronRight" size="sm" className="app-group__chevron" />
              </button>
              {isOpen(group.key) && !collapsed ? (
                <div className="app-group__items" id={`nav-${group.key}`}>
                  {group.items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="app-nav__item"
                      activeOptions={{ exact: item.to === '/' }}
                      activeProps={{ className: 'app-nav__item app-nav__item--on' }}
                      lang={langFor(lang, item.label)}
                    >
                      {tLabel(lang, item.label)}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </nav>
        <button type="button" className="app-side__collapse" onClick={toggleCollapsed} aria-pressed={collapsed} title={t(lang, collapsed ? 'Expand menu' : 'Collapse menu')}>
          <Icon name="chevronLeft" />
          <span className="app-side__word">{t(lang, 'Collapse menu')}</span>
        </button>
      </aside>
      <button type="button" className="app-scrim" lang={lang} aria-label={t(lang, 'Close menu')} tabIndex={-1} onClick={close} />

      <div className="app-body">
        <header className="app-bar" lang={lang}>
          <Button
            variant="ghost"
            size="sm"
            className="app-bar__menu"
            ref={menu}
            aria-label={t(lang, 'Menu')}
            aria-expanded={drawer}
            aria-controls="app-side"
            onClick={() => setDrawer(true)}
          >
            <Icon name="menu" />
          </Button>
          {/* Which company you are acting for, at all times. Clicking it returns to the picker. */}
          <button
            type="button"
            className="wb-company"
            ref={companyButton}
            onClick={() => {
              // 🔴 The context is about the COMPANY, so leaving one invalidates it. Without this the
              // next company's nav is gated on the previous company's licence until a full reload.
              forgetCompanyContext();
              setCompany(null);
            }}
          >
            {/* The initial and the caret are decoration: the name beside them is what the button says. */}
            <span className="wb-company__mark" aria-hidden="true" lang={nameLang}>
              {(current?.name ?? '·').charAt(0).toUpperCase()}
            </span>
            {/* A company's name is data: Bangla when it is written in Bangla, English otherwise (langOf). */}
            <span className="wb-company__name" lang={nameLang}>
              {current?.name ?? t(lang, 'Choose a company')}
            </span>
            <span className="wb-company__caret" aria-hidden="true">▾</span>
          </button>
          {/*
            🔴 THE VAT PERIOD BESIDE THE COMPANY — DS-11b4 (F591; owner, 2026-10-07: the one containing today). Which
            month the books are posting into, and whether posting there is still open; it opens Periods. The server sends
            it only to somebody who may read periods (`context.service.ts`), so no permission is checked here. Not on a
            phone (app.css): the first row there is the company's name; Periods is in the drawer.
            🔴 `data-period`, NOT `data-status` (DS-11b4 review): on the route it links to, TanStack's `Link` spreads its own
            `data-status="active"` OVER the caller's, so on /periods — the screen that locks the month — a locked period
            was drawn in the open (success) colours under the word Locked.
          */}
          {context?.vatPeriod ? (
            <Link to="/periods" className="app-vat" data-period={context.vatPeriod.status}>
              {/* The space is its own text node: without it the link's name ran "2026-10Open". */}
              {t(lang, 'VAT period')} {context.vatPeriod.label}{' '}
              <span className="app-vat__status">{t(lang, context.vatPeriod.status === 'open' ? 'Open' : 'Locked')}</span>
            </Link>
          ) : null}
          {/* DS-11a2 (§1.4; F51): it says it opens a dialog, whether that is open, and its shortcut; the hint is the Mac's. */}
          <button
            type="button"
            className="app-search"
            aria-haspopup="dialog"
            aria-expanded={palette}
            aria-keyshortcuts="Control+K Meta+K"
            onClick={() => setPalette(true)}
          >
            <Icon name="search" size="sm" />
            <span className="app-search__text">{t(lang, 'Go to a section…')}</span>
            <kbd className="app-search__kbd" lang="en">
              {/Mac|iP(hone|ad)/.test(navigator.userAgent) ? '⌘K' : 'Ctrl K'}
            </kbd>
          </button>
          {/* DS-11b3 (F530): says so while the browser is offline; an empty live region otherwise (OnlinePill.tsx). */}
          <OnlinePill label={t(lang, OFFLINE)} />
          <div className="app-bar__spacer" />
          {/*
            🔴 THE TOGGLE. Two languages, so it is a switch rather than a menu — and it shows the
            language you would GET, in that language's own script, which is how a reader who cannot
            read the current one finds their way out. ⚠️ NOT `aria-pressed` (DS-11a1, F402): its name is the
            language it offers, so "English, pressed" in Bangla said the opposite of what was on. It stays in the bar
            (DS-11a3): somebody who cannot read the current language must not have to open a menu to find the way out.
          */}
          <LangToggle lang={lang} />
          {/* DS-11b2: Help (WCAG 3.2.6; owner, 2026-10-07): the keys, Ctrl K among them, and who to ask. */}
          <UserMenu
            label={t(lang, 'Account')}
            lang={lang}
            labels={{ theme: t(lang, 'Theme'), system: t(lang, 'System'), light: t(lang, 'Light'), dark: t(lang, 'Dark'), signOut: t(lang, 'Sign out'), help: t(lang, 'Help') }}
            links={menuLinks}
            help={{ ask: t(lang, 'Ask an administrator of your company.'), palette: true, skip: t(lang, 'Skip to content') }}
            // DS-11b4 (F600): opening What's new marks the newest notes read, here and in this browser's storage.
            news={{
              label: t(lang, "What's new"),
              freshLabel: t(lang, 'New'),
              fresh,
              open: () =>
                whatsNew().then((m) => {
                  m.markSeen();
                  setFresh(false);
                  return m.WHATS_NEW;
                }),
            }}
            mark={unread}
            onSignOut={signOut}
          />
        </header>
        {/* DS-11a1: where the skip link and every navigation put focus (§1.1). */}
        <main className="app-main" id="main" tabIndex={-1}>
          {/*
            🔴 THE BOUNDARY EVERY LAZY ROUTE NEEDS. Without it React throws on the first navigation
            to a chunk that has not arrived, and the page goes white rather than waiting. The fallback
            is a Skeleton in the place the content will appear (DS-6a): nothing for its first 150ms, so
            a chunk that arrives quickly never flashes, and named, so a screen reader hears the wait.
          */}
          <Suspense fallback={<Skeleton label="the page" name={lang === 'bn' ? t(lang, 'Loading…') : undefined} lang={lang} />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      {/* Drawn into <body> by the palette itself, outside this tree (a modal <dialog>, DS-11a2). */}
      {palette ? (
        <Suspense fallback={null}>
          <CommandPalette groups={groups} lang={lang} onClose={() => setPalette(false)} />
        </Suspense>
      ) : null}
    </div>
  );
}

/**
 * 🔴 THE LANGUAGE TOGGLE (DS-4, DS-11a1 F402), in the bar and, since DS-11b2, on the signed-out frame (App.tsx), so
 * the way into Bangla is there before signing in. It shows the language you would GET, in that language's own script,
 * and carries that language (`lang`, WCAG 3.1.2), because a reader who cannot read the current one must find the way
 * out; NOT `aria-pressed`: its name is the language it offers.
 */
export function LangToggle({ lang }: { lang: Lang }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      lang={lang === 'bn' ? 'en' : 'bn'}
      onClick={() => setLang(lang === 'bn' ? 'en' : 'bn')}
    >
      {LANG_LABEL[lang === 'bn' ? 'en' : 'bn']}
    </Button>
  );
}

/** Per-person sidebar memory. Storage can be missing or refuse (private windows), so every access is guarded. */
function remembered(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A convenience; the shell works without it.
  }
}

/** The primary navigation, next to the routes so the two cannot disagree. */

/**
 * 🔴 THE SHELL, FORMATTED AS ITS COMPANY — design-system plan DS-7a (§2).
 *
 * Every figure and date under the shell — the screens, the palette, the chrome — reads `CompanyFormatContext`, filled
 * here from the session company's `/me/memberships` row (the same cached answer the shell's company button reads) and
 * `/me/currencies`. ⚠️ Around `Shell` rather than inside it: the shell's own specs render the chrome alone, and the
 * provider is a layer with its own spec (`company-format-provider.spec.tsx`).
 */
function FormattedShell() {
  /**
   * DS-9b: the kit's way back (BackLink) draws with the app's link; the kit itself imports no router. The context's own
   * Provider rather than `RouteLinkProvider`: this is the first load, and the wrapper would add a function to it.
   *
   * 🔴 DS-10d: and every `DocumentList` keeps its filters in the URL through the app's `useListSearch`, handed to the
   * kit the same way. Without this provider the sixteen lists with no `search` prop of their own (Invoices, Bills …)
   * fell back to their own state: the routes validated a search nothing wrote, and Back, a reload and a shared link
   * lost the filters. list-search-wiring.spec.tsx mounts a plain list under THIS tree to hold it. React 19's
   * `<Context value>` rather than `.Provider`: nine bytes, and this load has none to spare.
   */
  return (
    <RouteLinkContext.Provider value={Link}>
      <ListSearchContext value={useListSearch}>
        <CompanyFormatProvider>
          <Shell />
        </CompanyFormatProvider>
      </ListSearchContext>
    </RouteLinkContext.Provider>
  );
}

const rootRoute = createRootRoute({ component: FormattedShell });

/**
 * 🔴 ONE GUARD PER MODULE, NOT ONE PER ROUTE — ERP Phase 1a increment 9.
 *
 * ## ⚠️ IT WAS 104 COPIES OF THE SAME LINE
 *
 * Increment 4 gave every route `beforeLoad: guardModule('<its own path>')`, and the guard looked the
 * path up in `ROUTE_MODULES` to find the module. 🔬 Fifty-nine of those guards could never refuse
 * anything — their routes are `core`, which every company has — and the other forty-five each
 * repeated a decision that belongs to the MODULE: whether this company bought it. Nine modules, so
 * nine guards.
 *
 * So each gated module is a pathless layout route, its guard runs once for everything beneath it,
 * and a route's module is WHERE IT SITS rather than a string it repeats. `core` routes stay on the
 * root with no guard at all, because there is nothing to ask.
 *
 * ## 🔴 THE TREE IS CHECKED AGAINST THE SERVER, NOT TRUSTED
 *
 * `ROUTE_MODULES` is generated from the server's `@RequiresModule` decorators. `layouts.spec.tsx`
 * walks this tree and fails if any route sits under a layout other than the one its generated module
 * names — so a screen that changes module on the server cannot keep its old guard here.
 *
 * ⚠️ A pathless parent changes a route's ID, not its URL: `/bank-statements` is still the address,
 * but `useParams({ from })` names it `/_banking/bank-statements`. The type checker holds every one of
 * those, because the router is registered below.
 *
 * 🔴 AND THE TEST EARNED ITS PLACE BEFORE THIS SHIPPED. The script that nested the tree read module
 * keys as `[a-z-]+`, which does not match `fixed_assets` — so the register, the asset detail and the
 * depreciation runs lost their guard and landed on the root as `core`. The typecheck passed, since
 * nothing about a type says which module a screen is in; `layouts.spec.tsx` named all three.
 */
const bankingLayout = createRoute({ getParentRoute: () => rootRoute, id: '_banking', beforeLoad: guardModule('banking') });
const fixedAssetsLayout = createRoute({ getParentRoute: () => rootRoute, id: '_fixed_assets', beforeLoad: guardModule('fixed_assets') });
const crmLayout = createRoute({ getParentRoute: () => rootRoute, id: '_crm', beforeLoad: guardModule('crm') });
const inventoryLayout = createRoute({ getParentRoute: () => rootRoute, id: '_inventory', beforeLoad: guardModule('inventory') });
const payrollLayout = createRoute({ getParentRoute: () => rootRoute, id: '_payroll', beforeLoad: guardModule('payroll') });
const posLayout = createRoute({ getParentRoute: () => rootRoute, id: '_pos', beforeLoad: guardModule('pos') });
// ERP Phase 16 increment 4: the construction pack.
// ERP Phase 16 increment 5: funds, the NGO / donor pack — core, like the ledger dimension.
const FundsScreen = lazy(() => import('./screens/Funds').then((m) => asDefault(m.FundsScreen)));
const ConstructionScreen = lazy(() => import('./screens/Construction').then((m) => asDefault(m.ConstructionScreen)));
const projectsLayout = createRoute({ getParentRoute: () => rootRoute, id: '_projects', beforeLoad: guardModule('projects') });
const purchasingLayout = createRoute({ getParentRoute: () => rootRoute, id: '_purchasing', beforeLoad: guardModule('purchasing') });
const salesLayout = createRoute({ getParentRoute: () => rootRoute, id: '_sales', beforeLoad: guardModule('sales') });
// ERP Phase 13 increment 5b: the consolidation company's screens.
const groupLayout = createRoute({ getParentRoute: () => rootRoute, id: '_group', beforeLoad: guardModule('group') });
// ERP Phase 14: manufacturing-lite.
const manufacturingLayout = createRoute({ getParentRoute: () => rootRoute, id: '_manufacturing', beforeLoad: guardModule('manufacturing') });
// ERP Phase 18: the online store's payments.
const storefrontLayout = createRoute({ getParentRoute: () => rootRoute, id: '_storefront', beforeLoad: guardModule('storefront') });
// ERP Phase 15b: AMC and subscription contracts.
const subscriptionsLayout = createRoute({ getParentRoute: () => rootRoute, id: '_subscriptions', beforeLoad: guardModule('subscriptions') });

/** The layouts by module, for the test that holds the tree to the generated map. */
export const MODULE_LAYOUTS = {
  banking: bankingLayout,
  crm: crmLayout,
  fixed_assets: fixedAssetsLayout,
  group: groupLayout,
  manufacturing: manufacturingLayout,
  subscriptions: subscriptionsLayout,
  storefront: storefrontLayout,
  inventory: inventoryLayout,
  payroll: payrollLayout,
  pos: posLayout,
  projects: projectsLayout,
  purchasing: purchasingLayout,
  sales: salesLayout,
} as const;

// The root is the dashboard now. It used to redirect to the invoice list, on the grounds that a
// redirect was more honest than an empty page — which it was, and is why this route did not get a
// placeholder in the meantime.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardScreen,
});

const invoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices',
  validateSearch: documentListSearch,
  component: InvoicesScreen,
});

/**
 * BEFORE the `$invoiceId` route, and the order is load-bearing.
 *
 * A static segment and a dynamic one at the same depth are both candidates for `/invoices/new`.
 * TanStack Router ranks static above dynamic, so this is correct either way — but the two sitting
 * adjacent in the file is what makes that visible to whoever moves one of them.
 */
const invoiceNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/new',
  component: InvoiceCreateScreen,
});

const invoiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/$invoiceId',
  component: function InvoiceDetail() {
    const { invoiceId } = useParams({ from: '/invoices/$invoiceId' });
    return <InvoiceScreen invoiceId={invoiceId} />;
  },
});

/**
 * The editor gets its own URL, under the document rather than beside it.
 *
 * `/invoices/:id/edit` rather than `?edit=1` on the detail screen: an edit in progress is a
 * different thing to be looking at, and the back button should leave it rather than clear a query
 * parameter. It also means "the draft I was fixing" is a link, which is the same argument every
 * other route here was chosen on.
 */
const invoiceEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/$invoiceId/edit',
  component: function InvoiceEdit() {
    const { invoiceId } = useParams({ from: '/invoices/$invoiceId/edit' });
    return <InvoiceEditScreen invoiceId={invoiceId} />;
  },
});

// ── Bills, the purchase side ────────────────────────────────────────────────────────────────────
// `/bills/new` before `/bills/$billId`, for the same reason the invoice pair is ordered that way:
// both are candidates for the same path, and keeping them adjacent is what makes the ranking
// visible to whoever moves one.
const billNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bills/new',
  component: BillCreateScreen,
});

const billsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bills',
  validateSearch: documentListSearch,
  component: BillsScreen,
});

const billRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bills/$billId',
  component: function BillDetail() {
    const { billId } = useParams({ from: '/bills/$billId' });
    return <BillScreen billId={billId} />;
  },
});

const billEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bills/$billId/edit',
  component: function BillEdit() {
    const { billId } = useParams({ from: '/bills/$billId/edit' });
    return <BillEditScreen billId={billId} />;
  },
});


// ── Purchase orders ─────────────────────────────────────────────────────────────────────────────
// 🔴 THE FIRST DOCUMENT ROUTES IN THIS APP FOR SOMETHING THAT POSTS NOTHING. A purchase order is a
// commitment; the ledger moves at the goods receipt and again at the bill. The screens say so.
// `/purchase-orders/new` BEFORE `/purchase-orders/$orderId`, the same ordering the invoice and
// bill pairs use: both are candidates for the same path, and keeping them adjacent is what makes
// the ranking visible to whoever moves one.
const purchaseOrderNewRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/purchase-orders/new',
  component: PurchaseOrderCreateScreen,
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/purchase-orders',
  validateSearch: documentListSearch,
  component: PurchaseOrdersScreen,
});

const purchaseOrderRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/purchase-orders/$orderId',
  component: function PurchaseOrderDetail() {
    const { orderId } = useParams({ from: '/_purchasing/purchase-orders/$orderId' });
    return <PurchaseOrderScreen orderId={orderId} />;
  },
});


// ── Goods receipts ──────────────────────────────────────────────────────────────────────────────
// 🔴 THE DOCUMENT THAT TURNS THE COMMITMENT INTO A BALANCE. The receive form hangs off the ORDER,
// because a delivery is checked against what was agreed rather than composed from nothing.
const goodsReceiptsRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/goods-receipts',
  validateSearch: documentListSearch,
  component: GoodsReceiptsScreen,
});

const goodsReceiptRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/goods-receipts/$receiptId',
  component: function GoodsReceiptDetail() {
    const { receiptId } = useParams({ from: '/_purchasing/goods-receipts/$receiptId' });
    return <GoodsReceiptScreen receiptId={receiptId} />;
  },
});

const goodsReceiptCreateRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/purchase-orders/$orderId/receive',
  component: function GoodsReceiptCreate() {
    const { orderId } = useParams({ from: '/_purchasing/purchase-orders/$orderId/receive' });
    return <GoodsReceiptCreateScreen orderId={orderId} />;
  },
});


// ── The post-dated cheque register ──────────────────────────────────────────────────────────────
// 🔴 NOTHING ON THESE SCREENS IS IN THE LEDGER UNTIL A CHEQUE CLEARS. The register exists so a
// promise can be SEEN without being BOOKED, and every screen says so in words.
const chequeNewRoute = createRoute({
  getParentRoute: () => bankingLayout,
  path: '/post-dated-cheques/new',
  component: PostDatedChequeCreateScreen,
});

const chequesRoute = createRoute({
  getParentRoute: () => bankingLayout,
  path: '/post-dated-cheques',
  validateSearch: listSearch('direction'),
  component: PostDatedChequesScreen,
});

const chequeRoute = createRoute({
  getParentRoute: () => bankingLayout,
  path: '/post-dated-cheques/$chequeId',
  component: function ChequeDetail() {
    const { chequeId } = useParams({ from: '/_banking/post-dated-cheques/$chequeId' });
    return <PostDatedChequeScreen chequeId={chequeId} />;
  },
});


// ── Fixed assets ────────────────────────────────────────────────────────────────────────────────
// 🔴 THE REGISTER PRINTS THE SCHEDULE, which is §5's exit criterion in one route: an asset of
// 1,20,000 at a dated 10% straight line shows year-1 depreciation of 6,000.0000. The schedule is a
// READ; what is actually charged is decided by a run.
const fixedAssetsRoute = createRoute({
  getParentRoute: () => fixedAssetsLayout,
  path: '/fixed-assets',
  validateSearch: documentListSearch,
  component: FixedAssetsScreen,
});

const fixedAssetRoute = createRoute({
  getParentRoute: () => fixedAssetsLayout,
  path: '/fixed-assets/$assetId',
  component: function FixedAssetDetail() {
    const { assetId } = useParams({ from: '/_fixed_assets/fixed-assets/$assetId' });
    return <FixedAssetScreen assetId={assetId} />;
  },
});

// ⚠️ NOT under /fixed-assets: charging depreciation is something you DO, monthly, and it needs the
// key that posts rather than the one that keeps the register. Same reasoning as /periods.
const depreciationRoute = createRoute({
  getParentRoute: () => fixedAssetsLayout,
  path: '/depreciation',
  component: DepreciationRunsScreen,
});


// ── Blanket orders ──────────────────────────────────────────────────────────────────────────────
// 🔴 THE CONTRACT BEHIND THE PRICE. A supplier price list is what one party published; this is what
// both agreed, and a release priced away from it is refused. What is LEFT is the question it exists
// to answer, and the detail screen is where it is answered.
// ERP Phase 16 increment 2: the RMG export pack — master LCs, back-to-back, UD, EXP and the bond.
const exportLcsRoute = createRoute({ getParentRoute: () => purchasingLayout, path: '/export-lcs', component: ExportLcsScreen });

const blanketOrdersRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/blanket-orders',
  validateSearch: documentListSearch,
  component: BlanketOrdersScreen,
});

const blanketOrderRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/blanket-orders/$agreementId',
  component: function BlanketOrderDetail() {
    const { agreementId } = useParams({ from: '/_purchasing/blanket-orders/$agreementId' });
    return <BlanketOrderScreen agreementId={agreementId} />;
  },
});


// ── Letters of credit ───────────────────────────────────────────────────────────────────────────
// 🔴 A REGISTER, NOT A LEDGER. A bank opening a credit undertakes to pay the supplier; it does not
// move this firm's books. The margin is a fact recorded here and an entry raised elsewhere.
const lettersOfCreditRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/letters-of-credit',
  validateSearch: documentListSearch,
  component: LettersOfCreditScreen,
});

const letterOfCreditRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/letters-of-credit/$creditId',
  component: function LetterOfCreditDetail() {
    const { creditId } = useParams({ from: '/_purchasing/letters-of-credit/$creditId' });
    return <LetterOfCreditScreen creditId={creditId} />;
  },
});


// ── Landed costs ────────────────────────────────────────────────────────────────────────────────
// 🔴 THE ONE DOCUMENT THAT CHANGES WHAT STOCK IS WORTH WITHOUT CHANGING WHAT IS THERE. Part of the
// cost lands on goods still on the shelf and part on goods already sold — and the second part is an
// expense, immediately.
const landedCostsRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/landed-costs',
  validateSearch: documentListSearch,
  component: LandedCostsScreen,
});

const landedCostRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/landed-costs/$voucherId',
  component: function LandedCostDetail() {
    const { voucherId } = useParams({ from: '/_purchasing/landed-costs/$voucherId' });
    return <LandedCostScreen voucherId={voucherId} />;
  },
});


// ── Stock transfers ─────────────────────────────────────────────────────────────────────────────
// 🔴 TWO POSTINGS, BECAUSE `in_transit` IS A PLACE RATHER THAN A WORKFLOW STATE. Goods that have left
// and not arrived are sitting in a transit warehouse, on the books and on nobody's shelf — which is
// the honest position for a van on the road.
const stockTransfersRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/stock-transfers',
  validateSearch: documentListSearch,
  component: StockTransfersScreen,
});

const stockTransferRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/stock-transfers/$transferId',
  component: function StockTransferDetail() {
    const { transferId } = useParams({ from: '/_inventory/stock-transfers/$transferId' });
    return <StockTransferScreen transferId={transferId} />;
  },
});

// ⚠️ A WORKLIST RATHER THAN A REPORT — somebody walks the shelves with it, which is why the API
// refuses to truncate it and this screen shows that refusal instead of a first page.
// ERP Phase 16 increment 3: the pharma pack — the DGDA register, FEFO and the recall trace.
const pharmaRoute = createRoute({ getParentRoute: () => inventoryLayout, path: '/pharma', component: PharmaScreen });

const stockExpiryRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/reports/stock-expiry',
  component: StockExpiryScreen,
});

// ERP Phase 13 increment 6: items at or below their reorder level.
const reorderRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/reports/reorder',
  validateSearch: listSearch('warehouseCode'),
  component: ReorderReportScreen,
});

// ERP Phase 13 increment 7: the stock and sales dashboards, each under its own module.
const stockDashboardRoute = createRoute({ getParentRoute: () => inventoryLayout, path: '/dashboards/stock', component: StockDashboardScreen });
const salesDashboardRoute = createRoute({ getParentRoute: () => salesLayout, path: '/dashboards/sales', component: SalesDashboardScreen });

// ERP Phase 13 increment 5b: the group's consolidated statements.
const consolidatedRoute = createRoute({
  getParentRoute: () => groupLayout,
  path: '/reports/consolidated',
  component: ConsolidatedStatementsScreen,
});

const stockValuationRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/reports/stock-valuation',
  component: StockValuationScreen,
});

// 🔴 A VERDICT RATHER THAN A REPORT. The two books that hold the same money are maintained by
// different code on every document that moves goods, and when they disagree nothing else looks
// wrong — an invoice still posts and the balance sheet still balances. This is the one screen
// whose headline is a yes or a no.
const stockTieRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/stock-tie',
  component: StockTieScreen,
});

// ⚠️ BOTH DATES ARE REQUIRED BY THE API, and the screen defaults them to the year to date rather
// than opening on a refusal. A movement report with no start is the whole history of the company.
const stockMovementRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/reports/stock-movement',
  component: StockMovementScreen,
});

// 🔴 A LOOKUP, NOT A REPORT, which is why it is not under /reports: it answers a question about
// ONE object while somebody is standing at a counter holding it. ⚠️ And a unit's warehouse is
// where it was RECORDED — the serial register does not move stock, and the screen says so.
const trackingRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/tracking',
  component: TrackingScreen,
});

// 🔴 A WIZARD, NOT A FORM, and the difference is the suspense account. Four routes that run ONCE,
// in order, leaving a balance that must net to ZERO when the last of them lands — so the screen is
// built around that one figure and shows it after every step.
const conversionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/conversion',
  component: ConversionScreen,
});


// ── Stock counts and adjustments ────────────────────────────────────────────────────────────────
// 🔴 TWO DOCUMENTS, NOT ONE. The count is EVIDENCE — somebody walked the aisle — and the adjustment
// is what the books do about it, with its own reason and its own journal. A count that finds nothing
// wrong posts nothing at all.
const stockCountsRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/stock-counts',
  validateSearch: documentListSearch,
  component: StockCountsScreen,
});

const stockCountRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/stock-counts/$countId',
  component: function StockCountDetail() {
    const { countId } = useParams({ from: '/_inventory/stock-counts/$countId' });
    return <StockCountScreen countId={countId} />;
  },
});

const stockAdjustmentsRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/stock-adjustments',
  validateSearch: documentListSearch,
  component: StockAdjustmentsScreen,
});

const stockAdjustmentRoute = createRoute({
  getParentRoute: () => inventoryLayout,
  path: '/stock-adjustments/$adjustmentId',
  component: function StockAdjustmentDetail() {
    const { adjustmentId } = useParams({ from: '/_inventory/stock-adjustments/$adjustmentId' });
    return <StockAdjustmentScreen adjustmentId={adjustmentId} />;
  },
});


// Month-end. Not under /settings: closing a month is something you DO, monthly, not something you
// configure once — and it belongs beside the journals it freezes.
const periodsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/periods', component: PeriodsScreen });

// Payroll. Behind its own permission because `viewer` is defined as read-only access with NO
// salary data — seeing every journal does not entitle somebody to see what each person earns.
const payrollRunsRoute = createRoute({ getParentRoute: () => payrollLayout, path: '/payroll', component: PayrollRunsScreen });
const payrollRunRoute = createRoute({
  getParentRoute: () => payrollLayout,
  path: '/payroll/$runId',
  component: function PayrollRunDetail() {
    const { runId } = useParams({ from: '/_payroll/payroll/$runId' });
    return <PayrollRunScreen runId={runId} />;
  },
});
const recurringRoute = createRoute({ getParentRoute: () => rootRoute, path: '/recurring-costs', component: RecurringCostsScreen });

// Projects and the records time depends on. A project is addressed by CODE rather than by id,
// because every route in this group is `/projects/:code` — the code is the identity here.
const projectsRoute = createRoute({ getParentRoute: () => projectsLayout, path: '/projects', component: ProjectsScreen });
const projectRoute = createRoute({
  getParentRoute: () => projectsLayout,
  path: '/projects/$projectCode',
  component: function ProjectDetail() {
    const { projectCode } = useParams({ from: '/_projects/projects/$projectCode' });
    return <ProjectScreen projectCode={projectCode} />;
  },
});
const activityTypesRoute = createRoute({ getParentRoute: () => projectsLayout, path: '/activity-types', component: ActivityTypesScreen });
const constructionRoute = createRoute({ getParentRoute: () => projectsLayout, path: '/construction', component: ConstructionScreen });
const employeesRoute = createRoute({ getParentRoute: () => payrollLayout, path: '/employees', component: EmployeesScreen });
// ERP Phase 12 increment 1b — departments, designations and grades, under payroll with the employees they organise.
const organisationRoute = createRoute({ getParentRoute: () => payrollLayout, path: '/organisation', component: OrganisationScreen });
// ERP Phase 12 increment 2b — shifts and the roster, under payroll with the employees they roster.
const shiftsRoute = createRoute({ getParentRoute: () => payrollLayout, path: '/shifts', component: ShiftsScreen });
// ERP Phase 12 increment 3c — the daily sheet, punches and device files.
const attendanceRoute = createRoute({ getParentRoute: () => payrollLayout, path: '/attendance', component: AttendanceScreen });
// ERP Phase 12 increment 4 — holiday lists, the calendars the daily sheet reads.
const holidaysRoute = createRoute({ getParentRoute: () => payrollLayout, path: '/holidays', component: HolidaysScreen });
// The owner's Q4 (2026-09-26) — the wage-board floors, the company's board, and a worker under another.
const wageBoardsRoute = createRoute({ getParentRoute: () => payrollLayout, path: '/wage-boards', component: WageBoardsScreen });
// ERP Phase 12 increment 14 — employee self-service: your own posted payslips.
const myPayslipsRoute = createRoute({ getParentRoute: () => payrollLayout, path: '/my-payslips', component: MyPayslipsScreen });
// ERP Phase 12 increment 9c — the loans-and-advances and fines registers the payroll recovers.
const loansFinesRoute = createRoute({ getParentRoute: () => payrollLayout, path: '/loans-fines', component: LoansFinesScreen });

// The approval queue and the audit trail. Both are read daily and neither configures anything,
// so they sit in the rail rather than under Settings.
const approvalsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/approvals', component: ApprovalsScreen });
const auditRoute = createRoute({ getParentRoute: () => rootRoute, path: '/audit-log', component: AuditLogScreen });
// ERP Phase 17 increment 7: what the installation's operator did, read by the company admin.
const operatorTrailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/operator-activity', component: OperatorTrailScreen });

// Bank reconciliation. '/bank' rather than '/bank-statements': the statement is the input, and the
// job is reconciling. Five of the six routes are reachable — a manual line match needs a ledger
// entry id no route publishes, which the detail screen names rather than drawing a dead control.
const bankRoute = createRoute({ getParentRoute: () => bankingLayout, path: '/bank', component: BankStatementsScreen });
const bankStatementRoute = createRoute({
  getParentRoute: () => bankingLayout,
  path: '/bank/$statementId',
  component: function BankStatementDetail() {
    const { statementId } = useParams({ from: '/_banking/bank/$statementId' });
    return <BankStatementScreen statementId={statementId} />;
  },
});

// The chart and the VAT codes: the vocabulary every document form draws on, and until now a list
// nobody could see or add to. Accounts sits under Settings' account-roles screen in importance but
// not in the rail — it is referenced constantly, so it gets its own entry.
const accountsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/accounts', component: AccountsScreen });
const taxCodesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/tax-codes', component: TaxCodesScreen });
// INSTALLATION-tier, not company-tier — creating an account is a different act from granting one
// access to a company, which is Settings -> People.
const usersRoute = createRoute({ getParentRoute: () => rootRoute, path: '/users', component: UsersScreen });

// Settings. A hub plus six sections, each behind its own permission — a section the caller may not
// see says so rather than erroring, which is what  treats a 403 as.
const settingsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings', component: SettingsScreen });
// ── Tills ───────────────────────────────────────────────────────────────────────────────────────
// Reached from Settings rather than from the navigation rail: a till is opened once when a shop is
// set up and looked at again when a counter moves. ⚠️ A REGISTER IS A POSITION, NOT A MACHINE —
// the tablet standing on it is a device, listed elsewhere, and swapping it must not renumber the
// receipts.
const registersRoute = createRoute({ getParentRoute: () => posLayout, path: '/pos/registers', component: RegistersScreen });
const posSessionsRoute = createRoute({ getParentRoute: () => posLayout, path: '/pos/sessions', component: SessionsScreen });
const settingsCompanyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/company', component: SettingsCompanyScreen });
const settingsNumberingRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/numbering', component: SettingsNumberingScreen });
const settingsAccountsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/accounts', component: SettingsAccountsScreen });
const settingsApprovalsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/approvals', component: SettingsApprovalsScreen });
const settingsMembersRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/members', component: SettingsMembersScreen });
const settingsPortalRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/portal', component: SettingsPortalScreen });
const settingsPreferencesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/preferences', component: SettingsPreferencesScreen });
// ERP Phase 15 increment 1: the fields a company asks every item or partner to carry.
const settingsCustomFieldsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/custom-fields', component: SettingsCustomFieldsScreen });
// ERP Phase 15 increment 3: where the company's changes are sent.
// ERP Phase 15 increment 4: machines that may call the API, and every call they made.
// ERP Phase 15 increment 5: what routes an order to a second approver besides its size.
// ERP Phase 15 increment 6: how the invoice and the receipt look.
const settingsDocumentsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/documents', component: SettingsDocumentsScreen });
const settingsWorkflowRulesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/workflow-rules', component: SettingsWorkflowRulesScreen });
const settingsApiTokensRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/api-tokens', component: SettingsApiTokensScreen });
const settingsWebhooksRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/webhooks', component: SettingsWebhooksScreen });
// ERP Phase 15 increment 1: the item master's own screen, which it never had.
const itemsRoute = createRoute({ getParentRoute: () => inventoryLayout, path: '/items', component: ItemsScreen });
// The ledger itself. No `/new` — a journal is never created directly: it is written by posting a
// document or a manual journal DRAFT, which is what `/journal-drafts` is for. So there is a list
// and a detail and deliberately nothing else.
const fundsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/funds', component: FundsScreen });
const journalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/journals',
  component: JournalsScreen,
});

const journalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/journals/$journalId',
  component: function JournalDetail() {
    const { journalId } = useParams({ from: '/journals/$journalId' });
    return <JournalScreen journalId={journalId} />;
  },
});

/**
 * The reports, and the ONE place in this app that uses SEARCH PARAMETERS.
 *
 * A report is defined by what it was run for: a trial balance without its date is not a trial
 * balance, and "look at the P&L for Q2" has to be a link somebody can send — the same argument
 * `web/README.md` makes for documents having a real URL each.
 *
 * Every parameter is OPTIONAL and every screen defaults it. That is deliberate on two counts: a
 * required search param would make `<Link to="/reports/trial-balance">` a type error everywhere,
 * including in the hub; and `validateSearch` returning `undefined` for a hand-typed
 * `?asOf=yesterday` means a rubbish URL falls back to today rather than throwing or reaching the
 * API to be refused.
 */
/**
 * ⚠️ A search value is NOT always a string, even when the URL plainly contains text.
 *
 * TanStack Router JSON-parses each value, so `?account=1100` arrives as the NUMBER 1100 and a
 * `typeof v === 'string'` guard silently drops it — the param vanishes from the URL and the screen
 * sits on its empty state as though nothing had been asked for. Found in a browser with account
 * code 1100; every account code in the seeded chart is numeric, and a code like `AR-01` would have
 * hidden this indefinitely.
 *
 * So both helpers coerce rather than test. Dates are unaffected in practice — `JSON.parse` throws
 * on `2026-06-01` and the value stays a string — but a bare year would not be, and relying on that
 * is relying on an accident.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const asText = (v: unknown): string | undefined => {
  if (typeof v === 'string') return v === '' ? undefined : v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return undefined;
};
const asDate = (v: unknown): string | undefined => {
  const s = asText(v);
  return s !== undefined && ISO_DATE.test(s) ? s : undefined;
};

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  component: ReportsScreen,
});

const trialBalanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/trial-balance',
  validateSearch: (s: Record<string, unknown>): { asOf?: string } => ({ asOf: asDate(s.asOf) }),
  component: TrialBalanceScreen,
});

const profitAndLossRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/profit-and-loss',
  validateSearch: (s: Record<string, unknown>): { from?: string; to?: string; by?: 'branch' } => ({
    from: asDate(s.from),
    to: asDate(s.to),
    by: s.by === 'branch' ? 'branch' : undefined,
  }),
  component: ProfitAndLossScreen,
});

const balanceSheetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/balance-sheet',
  validateSearch: (s: Record<string, unknown>): { asOf?: string; by?: 'branch' } => ({
    asOf: asDate(s.asOf),
    by: s.by === 'branch' ? 'branch' : undefined,
  }),
  component: BalanceSheetScreen,
});

// ERP Phase 13 increment 3: every company you belong to, each read in its own books.
const groupDashboardRoute = createRoute({ getParentRoute: () => rootRoute, path: '/group', component: GroupDashboardScreen });
// ERP Phase 14 increment 1: bills of material.
const bomsRoute = createRoute({ getParentRoute: () => manufacturingLayout, path: '/boms', component: BomsScreen });
// ERP Phase 15b increment 1: contracts invoiced once and earned month by month.
const storePaymentsRoute = createRoute({ getParentRoute: () => storefrontLayout, path: '/store-payments', component: StorePaymentsScreen });
const subscriptionsRoute = createRoute({ getParentRoute: () => subscriptionsLayout, path: '/subscriptions', component: SubscriptionsScreen });
// ERP Phase 15b increment 5: a Daraz statement reconciled to the orders pulled, and booked.
const marketplaceRoute = createRoute({ getParentRoute: () => salesLayout, path: '/marketplace', component: MarketplaceScreen });
// The owner's couriers (2026-09-26): Steadfast, Pathao and RedX parcels, tracked, and their payouts.
const couriersRoute = createRoute({ getParentRoute: () => salesLayout, path: '/couriers', component: CouriersScreen });

// ERP Phase 13 increment 9: output VAT by place of supply — one BIN, many places.
const vatByPlaceRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reports/vat-by-place', component: VatByPlaceScreen });
// ERP Phase 13 increment 8: the owner's summary — built for a phone first.
const ownerSummaryRoute = createRoute({ getParentRoute: () => rootRoute, path: '/summary', component: OwnerSummaryScreen });

// ERP Phase 13 increment 2: value moved between branches, through the INTER_BRANCH account.
const interBranchTransferRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ledger/inter-branch-transfer',
  component: InterBranchTransferScreen,
});

const generalLedgerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/general-ledger',
  validateSearch: (s: Record<string, unknown>): { account?: string; from?: string; to?: string } => ({
    // An account CODE, not a date. Coerced rather than type-tested — see `asText`. Not validated
    // beyond that, because the server owns whether the account exists and says so by name.
    account: asText(s.account),
    from: asDate(s.from),
    to: asDate(s.to),
  }),
  component: GeneralLedgerScreen,
});

const vatReturnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/vat-return',
  validateSearch: (
    s: Record<string, unknown>,
  ): { from?: string; to?: string; frequency?: string; openingCarryForward?: string } => ({
    from: asDate(s.from),
    to: asDate(s.to),
    // A LABEL on the return, not a period selector — the server says so. Anything else is dropped
    // rather than passed on, since the API only recognises these two.
    frequency: s.frequency === 'quarterly' || s.frequency === 'monthly' ? s.frequency : undefined,
    // An AMOUNT, and `asText` matters here more than anywhere: `?openingCarryForward=1500` is
    // JSON-parsed to the number 1500, and a string test would drop exactly the round figures
    // somebody is most likely to type.
    //
    // Writing it back, the router QUOTES it — the URL reads `openingCarryForward=%223000%22`. That
    // is TanStack preserving string-ness on a value that would otherwise parse as a number, and it
    // is correct: money here is an exact decimal string and must never make the round trip through
    // a float. Both the quoted and the hand-typed bare form read back fine. Do not "clean it up".
    openingCarryForward: asText(s.openingCarryForward),
  }),
  component: VatReturnScreen,
});

// v1.343: the Mushak-9.2 — an enlisted company's turnover-tax return. Its period is the screen's own state.
const turnoverReturnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/turnover-return',
  component: TurnoverReturnScreen,
});

// The partner statement. `side` and `partner` are search params rather than path segments even
// though the API puts them in the path: this is ONE screen with a toolbar, and making them path
// params would mean two routes and a partner code in the URL before the screen can render at all.
const statementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/statements',
  validateSearch: (
    s: Record<string, unknown>,
  ): { side?: string; partner?: string; from?: string; to?: string; currency?: string } => ({
    side: s.side === 'payable' || s.side === 'receivable' ? s.side : undefined,
    // A partner CODE. `asText` because a numeric code arrives as a number — the same trap the
    // general ledger's account code hit.
    partner: asText(s.partner),
    from: asDate(s.from),
    to: asDate(s.to),
    currency: asText(s.currency)?.toUpperCase().slice(0, 3),
  }),
  component: StatementScreen,
});

// The purchase-side twin of the aging reconciliation, and it carries the same argument: the rows
// are a list, the RECONCILIATION is the report. GRNI holds what has arrived and not been billed,
// and §5's exit criterion is that it prints at zero once the bill posts.
const receivedNotBilledRoute = createRoute({
  getParentRoute: () => purchasingLayout,
  path: '/reports/received-not-billed',
  component: ReceivedNotBilledScreen,
});

// Aging and the subledger reconciliation on ONE route: they are the same question asked twice,
// and a total nobody can check is worse than no total.
const agingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/aging',
  validateSearch: (s: Record<string, unknown>): { side?: string; asOf?: string } => ({
    side: s.side === 'payable' || s.side === 'receivable' ? s.side : undefined,
    asOf: asDate(s.asOf),
  }),
  component: AgingScreen,
});

const vatSummaryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/vat-summary',
  validateSearch: (s: Record<string, unknown>): { from?: string; to?: string } => ({
    from: asDate(s.from),
    to: asDate(s.to),
  }),
  component: VatSummaryScreen,
});

// Receipts and payments. ONE list for both directions, as with the notes below: a firm's cash
// movements are a single sequence. There is NO edit route — see the detail screen's docblock, which
// explains it on the page rather than leaving a reader to wonder why this document is different.
const paymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments',
  validateSearch: documentListSearch,
  component: PaymentsScreen,
});

// Before the $paymentId route, as with every other document pair.
const paymentNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments/new',
  component: PaymentCreateScreen,
});

const paymentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments/$paymentId',
  component: function PaymentDetail() {
    const { paymentId } = useParams({ from: '/payments/$paymentId' });
    return <PaymentScreen paymentId={paymentId} />;
  },
});

const paymentEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments/$paymentId/edit',
  component: function PaymentEdit() {
    const { paymentId } = useParams({ from: '/payments/$paymentId/edit' });
    return <PaymentEditScreen paymentId={paymentId} />;
  },
});

// Credit and debit notes. ONE list for all four cells of the 2x2: `side` says which book,
// `kind` says which direction, and a firm's corrections are a single sequence they want to
// look at together.
const creditNotesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/credit-notes',
  validateSearch: documentListSearch,
  component: CreditNotesScreen,
});

// Before the $noteId route, as with the other two document pairs.
const creditNoteNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/credit-notes/new',
  component: CreditNoteCreateScreen,
});

const creditNoteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/credit-notes/$noteId',
  component: function CreditNoteDetail() {
    const { noteId } = useParams({ from: '/credit-notes/$noteId' });
    return <CreditNoteScreen noteId={noteId} />;
  },
});

const creditNoteEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/credit-notes/$noteId/edit',
  component: function CreditNoteEdit() {
    const { noteId } = useParams({ from: '/credit-notes/$noteId/edit' });
    return <CreditNoteEditScreen noteId={noteId} />;
  },
});

// Expense claims. `/new` before `$claimId`, as with every other document pair.
const expensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/expenses',
  validateSearch: documentListSearch,
  component: ExpensesScreen,
});

const expenseNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/expenses/new',
  component: ExpenseCreateScreen,
});

const expenseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/expenses/$claimId',
  component: function ExpenseDetail() {
    const { claimId } = useParams({ from: '/expenses/$claimId' });
    return <ExpenseScreen claimId={claimId} />;
  },
});

const expenseEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/expenses/$claimId/edit',
  component: function ExpenseEdit() {
    const { claimId } = useParams({ from: '/expenses/$claimId/edit' });
    return <ExpenseEditScreen claimId={claimId} />;
  },
});

// Manual journals. ONE form component for create and edit — unlike every other document, there
// is nothing that differs between them: no number to allocate, no partner, no allocations.
const journalDraftsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/journal-drafts',
  validateSearch: documentListSearch,
  component: JournalDraftsScreen,
});

const journalDraftNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/journal-drafts/new',
  component: function JournalDraftNew() {
    return <JournalDraftFormScreen />;
  },
});

const journalDraftRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/journal-drafts/$draftId',
  component: function JournalDraftDetail() {
    const { draftId } = useParams({ from: '/journal-drafts/$draftId' });
    return <JournalDraftScreen draftId={draftId} />;
  },
});

const journalDraftEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/journal-drafts/$draftId/edit',
  component: function JournalDraftEdit() {
    const { draftId } = useParams({ from: '/journal-drafts/$draftId/edit' });
    return <JournalDraftFormScreen draftId={draftId} />;
  },
});

// The §45 register. Three statuses, no approval gate, and rows written by a NOTE that no route
// here may touch — see `VatAdjustment.tsx`.
const vatAdjustmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vat-adjustments',
  validateSearch: documentListSearch,
  component: VatAdjustmentsScreen,
});

const vatAdjustmentNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vat-adjustments/new',
  component: function VatAdjustmentNew() {
    return <VatAdjustmentFormScreen />;
  },
});

const vatAdjustmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vat-adjustments/$adjustmentId',
  component: function VatAdjustmentDetail() {
    const { adjustmentId } = useParams({ from: '/vat-adjustments/$adjustmentId' });
    return <VatAdjustmentScreen adjustmentId={adjustmentId} />;
  },
});

// The owner's decision of 2026-10-07: turnover tax is corrected by its own document, read by the Mushak-9.2 — see
// `TurnoverTaxAdjustments.tsx`. Create, post and void; no edit.
const turnoverTaxAdjustmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/turnover-tax-adjustments',
  validateSearch: documentListSearch,
  component: TurnoverTaxAdjustmentsScreen,
});

const turnoverTaxAdjustmentNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/turnover-tax-adjustments/new',
  component: function TurnoverTaxAdjustmentNew() {
    return <TurnoverTaxAdjustmentFormScreen />;
  },
});

const turnoverTaxAdjustmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/turnover-tax-adjustments/$adjustmentId',
  component: function TurnoverTaxAdjustmentDetail() {
    const { adjustmentId } = useParams({ from: '/turnover-tax-adjustments/$adjustmentId' });
    return <TurnoverTaxAdjustmentScreen adjustmentId={adjustmentId} />;
  },
});

const vatAdjustmentEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vat-adjustments/$adjustmentId/edit',
  component: function VatAdjustmentEdit() {
    const { adjustmentId } = useParams({ from: '/vat-adjustments/$adjustmentId/edit' });
    return <VatAdjustmentFormScreen adjustmentId={adjustmentId} />;
  },
});

// 🔴 LEADS SIT BEFORE PARTNERS IN THE RELATIONSHIP AND AFTER THEM IN THE MENU, which is not a
// contradiction: the menu is ordered by how often a screen is opened, and a company reads its
// customer list every day and its lead list when somebody is chasing.
const leadsRoute = createRoute({
  getParentRoute: () => crmLayout,
  path: '/leads',
  validateSearch: listSearch('source'),
  component: LeadsScreen,
});

const activitiesRoute = createRoute({
  getParentRoute: () => crmLayout,
  path: '/activities',
  validateSearch: listSearch('kind', 'done'),
  component: ActivitiesScreen,
});

/**
 * One customer, one page — ERP Phase 8 increment 7.
 *
 * ⚠️ UNDER `/partners/`, WITH NO NAV ENTRY, because it is a page you arrive at ABOUT somebody
 * rather than one you open cold: the customer list links here, and a 360 with nobody in it is not a
 * screen. The code is in the path rather than a search parameter for the reason `/invoices/$id` is:
 * *"the customer I was looking at"* should be a link somebody can send.
 */
/**
 * The pipeline board — ERP Phase 8 increment 6, and the first screen `opportunities` has had.
 *
 * ⚠️ IN THE NAV, unlike the 360: a board is a page somebody opens every morning to decide what to
 * chase, not one they arrive at about a particular customer.
 */
const pipelineRoute = createRoute({
  getParentRoute: () => crmLayout,
  path: '/pipeline',
  component: PipelineScreen,
});

const partner360Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partners/$code',
  component: function Partner360() {
    const { code } = useParams({ from: '/partners/$code' });
    return <Partner360Screen code={code} />;
  },
});

// 🔴 UNDER `/reports`, WITH THE EXPIRING-STOCK WORKLIST IT MOST RESEMBLES — both are unpaged,
// capped lists somebody works through rather than reads. The nav carries the LOG; this is the thing
// you open to find out what you owe.
const followUpsRoute = createRoute({
  getParentRoute: () => crmLayout,
  path: '/reports/follow-ups',
  component: FollowUpsScreen,
});

// ⚠️ THE REPORT AND NOT THE DRAWER. `GET /partner-documents` lists the filed paper, but its rows
// carry `partner_id` and `attachment_id` as UUIDs — `pageByKey` projects one table and does not join
// — so a list screen would print two uuids per row. The EXPIRY report is joined and resolved, and it
// is the one somebody opens anyway: what is filed matters less than what is running out.
const partnerDocumentExpiryRoute = createRoute({
  getParentRoute: () => crmLayout,
  path: '/reports/partner-document-expiry',
  component: PartnerDocumentExpiryScreen,
});

// 🔴 THE ONLY WAY TO SEE SALES ORDERS AT ALL. `sales-orders` has no list route — the controller
// serves one order by id, its snapshot, and the three transitions — so §7's central document has no
// index of its own. The order book is joined, resolved and capped, and it answers more than a list
// would: what was promised, what has gone, what has been billed and what is still owed.
const orderBookRoute = createRoute({
  getParentRoute: () => salesLayout,
  path: '/reports/order-book',
  component: OrderBookScreen,
});

const partnersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partners',
  component: PartnersScreen,
});


/**
 * 🔴 A DEEP LINK INTO A MODULE THIS COMPANY HAS NOT BOUGHT — ERP Phase 1a increment 4.
 *
 * §1a's second exit criterion: *"a screen behind a disabled module is absent from the nav and
 * answers the module-disabled state, **not a blank page**"*. The nav half is `visibleNav` below;
 * this is the other half, and it is the half that matters, because **the nav cannot stop a
 * bookmark**.
 *
 * ## ⚠️ ONCE PER MODULE SINCE INCREMENT 9, AND THE RISK THAT USED TO JUSTIFY 104 COPIES
 *
 * Increment 4 put this on EVERY route, core included, for a stated reason: guarding only where it was
 * needed would leave a route silently unguarded on the day somebody moved a controller into a
 * licensed module — and `ROUTE_MODULES` is REGENERATED from the server, so that day arrives without
 * anybody editing this file. 🔬 The reason still holds; the answer changed. It now runs on the nine
 * module layouts above, and `layouts.spec.tsx` compares the tree with the regenerated map — so the day
 * a screen changes module, that test goes red and names the route, instead of 104 lookups quietly
 * absorbing it.
 *
 * ## 🔴 AND IT IS NOT A SECURITY BOUNDARY
 *
 * `ModuleGuard` on the server is. A context that fails to load leaves every route OPEN here, on
 * purpose: the API still refuses what the company has not bought, so a wrong yes costs a dead link,
 * while a wrong no hides a screen the customer is paying for behind a network blip.
 */
function guardModule(module: string) {
  return async ({ location }: { location: { href: string } }) => {
    const context = await loadCompanyContext();
    if (context && !context.enabledModules.includes(module)) {
      throw redirect({ to: '/module-disabled', search: { module, from: location.href } });
    }
  };
}

const moduleDisabledRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/module-disabled',
  /**
   * ⚠️ The module and the original URL travel in the QUERY, so the state is bookmarkable and
   * shareable — a person forwarding *"this link does not work for me"* sends something that still
   * says which module it was.
   */
  validateSearch: (search: Record<string, unknown>) => ({
    module: String(search.module ?? ''),
    from: search.from === undefined ? undefined : String(search.from),
  }),
  component: function ModuleDisabled() {
    const { module, from } = useSearch({ from: '/module-disabled' });
    return <ModuleDisabledScreen module={module} from={from} />;
  },
});

export const routeTree = rootRoute.addChildren([
  moduleDisabledRoute,
  indexRoute,
  invoicesRoute,
  invoiceNewRoute,
  invoiceRoute,
  invoiceEditRoute,
  billsRoute,
  stockTieRoute,
  conversionRoute,
  billNewRoute,
  billRoute,
  billEditRoute,
  paymentsRoute,
  paymentNewRoute,
  paymentRoute,
  paymentEditRoute,
  journalsRoute,
  fundsRoute,
  journalRoute,
  periodsRoute,
  recurringRoute,
  approvalsRoute,
  auditRoute,
  operatorTrailRoute,
  accountsRoute,
  taxCodesRoute,
  usersRoute,
  settingsRoute,
  settingsCompanyRoute,
  settingsNumberingRoute,
  settingsAccountsRoute,
  settingsApprovalsRoute,
  settingsMembersRoute,
  settingsPortalRoute,
  settingsPreferencesRoute,
  settingsCustomFieldsRoute,
  settingsWebhooksRoute,
  settingsApiTokensRoute,
  settingsWorkflowRulesRoute,
  settingsDocumentsRoute,
  reportsRoute,
  trialBalanceRoute,
  profitAndLossRoute,
  balanceSheetRoute,
  interBranchTransferRoute,
  groupDashboardRoute,
  ownerSummaryRoute,
  vatByPlaceRoute,
  generalLedgerRoute,
  vatReturnRoute,
  turnoverReturnRoute,
  vatSummaryRoute,
  statementRoute,
  agingRoute,
  creditNotesRoute,
  creditNoteNewRoute,
  creditNoteRoute,
  creditNoteEditRoute,
  expensesRoute,
  expenseNewRoute,
  expenseRoute,
  expenseEditRoute,
  journalDraftsRoute,
  journalDraftNewRoute,
  journalDraftRoute,
  journalDraftEditRoute,
  vatAdjustmentsRoute,
  vatAdjustmentNewRoute,
  vatAdjustmentRoute,
  vatAdjustmentEditRoute,
  turnoverTaxAdjustmentsRoute,
  turnoverTaxAdjustmentNewRoute,
  turnoverTaxAdjustmentRoute,
  partnersRoute,
  partner360Route,
  bankingLayout.addChildren([chequeNewRoute, chequesRoute, chequeRoute, bankRoute, bankStatementRoute]),
  fixedAssetsLayout.addChildren([fixedAssetsRoute, fixedAssetRoute, depreciationRoute]),
  crmLayout.addChildren([leadsRoute, activitiesRoute, pipelineRoute, followUpsRoute, partnerDocumentExpiryRoute]),
  inventoryLayout.addChildren([itemsRoute, stockTransfersRoute, stockTransferRoute, stockValuationRoute, stockMovementRoute, trackingRoute, stockExpiryRoute, pharmaRoute, reorderRoute, stockDashboardRoute, stockCountsRoute, stockCountRoute, stockAdjustmentsRoute, stockAdjustmentRoute]),
  payrollLayout.addChildren([payrollRunsRoute, payrollRunRoute, employeesRoute, organisationRoute, shiftsRoute, attendanceRoute, holidaysRoute, wageBoardsRoute, myPayslipsRoute, loansFinesRoute]),
  posLayout.addChildren([registersRoute, posSessionsRoute]),
  groupLayout.addChildren([consolidatedRoute]),
  manufacturingLayout.addChildren([bomsRoute]),
  subscriptionsLayout.addChildren([subscriptionsRoute]),
  storefrontLayout.addChildren([storePaymentsRoute]),
  projectsLayout.addChildren([projectsRoute, projectRoute, activityTypesRoute, constructionRoute]),
  purchasingLayout.addChildren([purchaseOrderNewRoute, purchaseOrdersRoute, goodsReceiptCreateRoute, purchaseOrderRoute, goodsReceiptsRoute, goodsReceiptRoute, blanketOrdersRoute, blanketOrderRoute, lettersOfCreditRoute, letterOfCreditRoute, exportLcsRoute, landedCostsRoute, landedCostRoute, receivedNotBilledRoute]),
  salesLayout.addChildren([salesDashboardRoute, orderBookRoute, marketplaceRoute, couriersRoute]),
]);

/**
 * DS-6a: an address that leads nowhere, and a screen that throws while it draws, are said in Workbook's words INSIDE
 * the shell — the router's defaults were a bare "Not Found" and an unstyled "Something went wrong!" that replaced the
 * sidebar too. Try again draws the screen afresh.
 */
function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  if (isChunkFailure(error)) {
    // The screen's code is gone from the server (a release replaced it) or never arrived: asking again cannot work.
    return (
      <Stack as="section" className="screen">
        <ErrorState
          title="This screen could not be opened"
          error="Workbook was updated while this page was open, or the connection dropped. Reload to carry on."
          onRetry={() => window.location.reload()}
          retryLabel="Reload"
        />
      </Stack>
    );
  }
  return (
    <Stack as="section" className="screen">
      <ErrorState title="This screen could not be shown" error={error} onRetry={reset} />
    </Stack>
  );
}

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => <NotFound />,
  defaultErrorComponent: RouteError,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export { Link };
